import { TemplatePrompts } from '@kimuno/api-sdk';
import type { Location } from 'found';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { recordCampaignImpression, recordTriggeredHint } from '../actions/user.actions';
import { LAUNCH_PARAM_KEYS } from '../components/AIAssistant/shared/templateLaunchParams';
import {
	podcastLaunchCampaignId,
	promoBannerHintKey,
} from '../components/PromoBanner/promoCampaigns';
import { isOnStripeSchoolAdminArea } from '../components/StripeSchoolAdmin/isOnStripeSchoolAdminArea';
import { routes } from '../constants';
import { selectIsMeLoaded, selectTriggeredHints } from '../reducers/user.selectors';
import { useAppDispatch } from '../store/hooks';
import type { CampaignId } from './campaigns/registry';
import useAction from './useAction';

// Hints and banners that point the user at a destination, keyed by the exact
// `triggeredHints` key their visibility check reads. Reaching the destination
// concludes them for good, even if they were never displayed. Add an entry
// here for any new hint/banner that advertises a navigable target.
// Segment-boundary match: `/ki-tools` and `/ki-tools/<conversationId>` count,
// substring look-alikes (`/ki-toolsbar`, slugs containing "ki-tools") do not —
// a false positive would conclude a hint permanently.
const isOnAiToolsPage = ({ pathname }: Location) =>
	pathname === `/${routes.aiTools.root}` || pathname.startsWith(`/${routes.aiTools.root}/`);

export const targetRouteByHintKey: Record<string, (location: Location) => boolean> = {
	[promoBannerHintKey(podcastLaunchCampaignId)]: (location) =>
		isOnAiToolsPage(location) &&
		location.query?.[LAUNCH_PARAM_KEYS.template] === TemplatePrompts.CREATE_PODCAST,
};

// Same idea for proactive campaigns: reaching the destination records a permanent
// dismissal, so a campaign pointing at a page the user already found never shows.
export const targetRouteByCampaignId = {
	'school-admin-hint': ({ pathname }: Location) => isOnStripeSchoolAdminArea(pathname),
} satisfies Partial<Record<CampaignId, (location: Location) => boolean>>;

export const useConcludeHintOnTargetRoute = (location: Location) => {
	const triggeredHints = useSelector(selectTriggeredHints);
	const recordTriggeredHintAction = useAction(recordTriggeredHint);
	const isMeLoaded = useSelector(selectIsMeLoaded);
	const dispatch = useAppDispatch();

	// biome-ignore lint/correctness/useExhaustiveDependencies: recordTriggeredHintAction is recreated each render (useAction is unmemoized) but only wraps stable dispatch
	useEffect(() => {
		for (const [hintKey, isOnTargetRoute] of Object.entries(targetRouteByHintKey)) {
			if (!triggeredHints.includes(hintKey) && isOnTargetRoute(location)) {
				recordTriggeredHintAction(hintKey);
			}
		}
	}, [location, triggeredHints]);

	// Keyed on `isMeLoaded` so a deep link that renders before `/me` resolves still
	// records. The thunk skips a campaign that is already dismissed.
	useEffect(() => {
		if (!isMeLoaded) {
			return;
		}
		for (const [campaignId, isOnTargetRoute] of Object.entries(targetRouteByCampaignId)) {
			if (isOnTargetRoute(location)) {
				dispatch(recordCampaignImpression({ campaignId, action: 'dismiss' }));
			}
		}
	}, [location, isMeLoaded, dispatch]);
};

export default useConcludeHintOnTargetRoute;
